import moment from "moment";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";

import {
  createListCollection,
  EmptyState,
  GridItem,
  IconButton,
  Image,
  Input,
  Portal,
  Select,
  SimpleGrid,
  Table,
} from "@chakra-ui/react";
import { format } from "date-fns";
import { LuX } from "react-icons/lu";
import { useNavigate, useParams } from "react-router-dom";
import useAlert from "src/hooks/use-alert";
import { useGiveOfferMutation } from "src/lib/endpoints/hire/hire.api";
import { Milestone } from "src/lib/endpoints/hire/hire.type";
import { useFundWalletMutation } from "src/lib/endpoints/wallet/wallet.api";
import DateTime from "src/lib/utils/date-time";
import { commaSeperatedNumberFormat } from "src/lib/utils/format-text";
import { getOrdinalSuffix } from "src/utils/utils";
import CustomAlertDialog from "../Alert/custom-alert-dialog";
import OutlineButton from "../Button/OutlineButton";
import OfferActions from "./OfferActions";
import MakeDepositModal from "../Modal/MakeDeposit";
import Status from "../Status/Status";
import Field from "../field";
import {
  OfferForm,
  OfferMilestonePayload,
} from "../../pages/ProjectSetup/types/OfferObject";
import { useTypedSelector } from "src/lib/store/store";
import { selectUserState } from "src/lib/features/auth/authSlice";

enum FieldsName {
  AMOUNT = "amount",
  INTERVALS = "intervals",
  PERIOD = "period",
  DATE = "date",
}

interface InputFields {
  amount: string;
  intervals: string;
  period: string;
  date: string;
}

const intervalOptions = ["month", "week"];

const intervalOptionsCollection = createListCollection({
  items: ["month", "week"],
  itemToValue: (item) => item,
});

const RetainerForm = ({
  currencySymbol = "$",
  description,
  jobDetail,
  isBasic,
  onDone,
}: {
  currencySymbol?: string,
  description: string;
  jobDetail: any;
  isBasic?: boolean;
  onDone: (data: OfferForm) => void;
}) => {
  const { jobId, userId } = useParams();
  const { giveOffer, isLoading } = useGiveOfferMutation({
    jobId: jobId ?? "",
    userId: userId ?? "",
  });
  const navigate = useNavigate();
  const [depositOpen, setDepositOpen] = useState(false);
  const [balance, setBalance] = useState(0);
  const [isCostValid, setIsCostValid] = useState(true);
  const [requiresDeposit, setRequiresDeposit] = useState(false);
  const [selectedInterval, setSelectedInterval] = useState("");
  const [tableData, setTableData] = useState<Milestone[]>([]);
  const [minDate, setMinDate] = useState(DateTime.getCurrentDate());
  const [percentage, setPercentage] = useState(0);
  const { fundWallet } = useFundWalletMutation(userId ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [open, setIsOpen] = useState(false);
  const [transactionId, setTransactionId] = useState("");
     const { user } = useTypedSelector(selectUserState);

  const [paymentGatewayUrl, setPaymentGatewayUrl] = useState("");
  const {
    state: alertState,
    open: openAlert,
    close: closeAlert,
    Alert,
  } = useAlert();
  const formMethods = useForm<InputFields>({
    defaultValues: {
      [FieldsName.AMOUNT]: "",
      [FieldsName.INTERVALS]: intervalOptions[0],
      [FieldsName.PERIOD]: "",
      [FieldsName.DATE]: "",
    },
  });

  const {
    control,
    handleSubmit,
    register,
    setValue,
    watch,
    reset,
    resetField,
    getValues,
    formState: { errors: formErrors },
  } = formMethods;

  const interval = watch().intervals;

  const submit = handleSubmit(async (data: InputFields) => {
    const { period, amount, date, intervals } = data;
    setSelectedInterval(intervals);
    const days = intervals === intervalOptions[0] ? 30 : 7;

    setTableData([]);
    let _tableData: Milestone[] = [];
    let curDate = new Date(date);

    for (let i = 1; i <= parseInt(period); i++) {
      let priorDate = moment(curDate).add(days, "days").format("YYYY-MM-DD");

      const curData = {
        amountPayable: amount,
        description: `${getOrdinalSuffix(i)} ${intervals}`,
        // startDate: new Date(curDate).toLocaleDateString(), // "2023-05-15T23:35:01",
        // endDate: new Date(priorDate).toLocaleDateString(), // "2023-05-15T23:35:01",
       startDate: moment(curDate).format("YYYY-MM-DD"),
       endDate: moment(priorDate).format("YYYY-MM-DD"),
        
        job_detail_id: jobId ?? "",
      };

      _tableData.push(curData);
      curDate = new Date(
        moment(curDate)
          .add(days + 1, "days")
          .format("YYYY-MM-DD")
      );
    }

    setTableData(_tableData);
    reset();
  });

  const handleDelete = () => {
    setTableData((prev) => prev.slice(0, tableData.length - 1));
  };

  const createOffer = () => {
    const milestones: OfferMilestonePayload[] = tableData.map((each) => {
      return {
        amountPayable: each.amountPayable,
        description: each.description,
        startDate: each.startDate.replace("Z", ""),
        endDate: each.endDate.replace("Z", ""),
        job_detail_id: jobId,
      };
    });

    const total = milestones.reduce(
      (total, value) => total + parseInt(value.amountPayable),
      0
    );

    // const budget = parseFloat(jobDetail?.project_budget ?? "0");

    // if (total > budget) {
    //   openAlert({
    //     ...alertState,
    //     title: `Total budget of ${total} exceeds Proposed budget of ${budget}`,
    //     variant: "info",
    //     active: true,
    //   });
    // }

    const interval = selectedInterval === intervalOptions[0] ? 30 : 7;

    // const formData = new FormData();
    // formData.append("description", description);
    // formData.append("work_type", "retainer");
    // formData.append("payment_type", "milestone-based");
    // formData.append("agreed_total_cost", `${total}`);
    // formData.append("startDate", tableData[0].startDate.replace("Z", ""));
    // formData.append("milestones", JSON.stringify(milestones));
    // formData.append("retainer_agreed_interval", selectedInterval);
    // formData.append("agreed_duration", `${tableData.length * interval}`);
    // formData.append(
    //   "retainer_agreed_interval_payment",
    //   `${tableData[0].amountPayable}`
    // );

    if (isBasic) {
      const formData = new FormData();
      formData.append("description", description);
      formData.append("work_type", "retainer");
      formData.append("payment_type", "milestone-based");
      formData.append("agreed_total_cost", `${total}`);
      formData.append("startDate", tableData[0].startDate.replace("Z", ""));
      formData.append("milestones", JSON.stringify(milestones));
      formData.append("retainer_agreed_interval", selectedInterval);
      formData.append("agreed_duration", `${tableData.length * interval}`);
      formData.append(
        "retainer_agreed_interval_payment",
        `${tableData[0].amountPayable}`
      );

      giveOffer(formData, {
        onSuccess: async (res) => {
          if (typeof res?.data?.amount === "number") {
            openAlert({
              ...alertState,
              title: res?.message ?? "",
              variant: "info",
              active: true,
            });
            setBalance(parseInt(res.data.amount));
            setPercentage(parseInt(res.data.percentage));
            setRequiresDeposit(true);
          } else if (res?.status) {
            openAlert({
              ...alertState,
              title: res?.message ?? "Offer sent successfully",
              variant: "success",
              active: true,
            });
            setTimeout(() => {
              navigate(`/project/${res?.data?.id ?? ""}`);
            }, 1500);
          }
        },
        onError: (error: any) => {
          openAlert({
            ...alertState,
            title: error?.response?.data?.message ?? error?.message ?? "",
            variant: "error",
            active: true,
          });
        },
      });
    } else {
      return onDone({
        description: description,
        work_type: "retainer",
        payment_type: "milestone-based",
        agreed_total_cost: total.toString(),
        startDate: tableData[0].startDate.replace("Z", ""),
        milestones: milestones,
        retainer_agreed_interval: selectedInterval,
        agreed_duration: (tableData.length * interval).toString(),
        retainer_agreed_interval_payment: tableData[0].amountPayable,
      });
    }
  };

  // const handleFundWallet = async () => {
  //   if (isSubmitting) return;
  //   setIsSubmitting(true);

  //   let formData = new FormData();
  //   formData.append("amount", balance.toString());
  //   formData.append("gateway", "paystack");

  //   await fundWallet(formData, {
  //     onSuccess: async (res) => {
  //       setIsSubmitting(false);
  //       setTransactionId(res?.transaction?.id);
  //       setPaymentGatewayUrl(res?.data?.authorization_url ?? "");
  //       // setPaymentGatewayUrl(res?.data?.link ?? "");
  //     },
  //     onError: (error: any) => {
  //       setIsSubmitting(false);
  //       openAlert({
  //         ...alertState,
  //         title: error?.response?.data?.message ?? error?.message ?? "",
  //         variant: "error",
  //         active: true,
  //       });
  //     },
  //   });
  // };

  const onNext = () => {
    createOffer();
  };

  // useEffect(() => {
  //   const total = tableData.reduce(
  //     (total, value) => total + parseInt(value.amountPayable),
  //     0
  //   );

  //   setIsCostValid(total <= parseFloat(jobDetail.project_budget));
  // }, [tableData]);

  return (
    <>
      <SimpleGrid gap={4} columns={[1, null, 2]}>
        <Field
          required
          label="Amount"
          invalid={!!formErrors[FieldsName.AMOUNT]}
          errorText={formErrors[FieldsName.AMOUNT]?.message}
        >
          <Input
            id={FieldsName.AMOUNT}
            {...register(FieldsName.AMOUNT, {
              required: "Amount is required",
              min: { value: 1, message: "Amount to low" },
            })}
            required={false}
            borderWidth={"thin"}
            px={3}
            rounded={"md"}
            placeholder="1000"
            type="number"
          />
        </Field>

        <Controller
          name="intervals"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <Field
                label="Payment Intervals"
                required
                invalid={!!formErrors[FieldsName.INTERVALS]}
                errorText={formErrors[FieldsName.INTERVALS]?.message}
              >
                <Select.Root
                  collection={intervalOptionsCollection}
                  value={[value]}
                  onValueChange={(value) => {
                    setSelectedInterval(value.value[0]);
                    onChange(value.value[0]);
                  }}
                >
                  <Select.HiddenSelect />
                  <Select.Control>
                    <Select.Trigger borderWidth={"thin"} rounded="md">
                      <Select.ValueText
                        textTransform={"capitalize"}
                        placeholder="Select"
                        px={3}
                      />
                    </Select.Trigger>
                  </Select.Control>
                  <Portal>
                    <Select.Positioner>
                      <Select.Content color="fg">
                        {intervalOptionsCollection.items.map((each) => (
                          <Select.Item
                            key={each}
                            item={each}
                            textTransform={"capitalize"}
                          >
                            {each}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Portal>
                </Select.Root>
              </Field>
            );
          }}
        />

        <Field
          required
          label="Retainership Duration"
          invalid={!!formErrors[FieldsName.PERIOD]}
          errorText={formErrors[FieldsName.PERIOD]?.message}
        >
          <Input
            id={FieldsName.PERIOD}
            {...register(FieldsName.PERIOD, {
              required: "Retainership Duration is required",
            })}
            placeholder={`2 ${
              interval === intervalOptions[0] ? "months" : "weeks"
            }`}
            type="number"
            className="w-full"
            required={false}
            borderWidth={"thin"}
            px={3}
            rounded={"md"}
          />
        </Field>

        <Field
          required
          label="Start Date"
          invalid={!!formErrors[FieldsName.DATE]}
          errorText={formErrors[FieldsName.DATE]?.message}
        >
          <Input
            id={FieldsName.DATE}
            {...register(FieldsName.DATE, {
              required: "Date is required",
            })}
            min={minDate}
            type="date"
            className="w-full"
            required={false}
            borderWidth={"thin"}
            px={3}
            rounded={"md"}
          />
        </Field>

        {/* Builds the schedule below; the orange button is saved for sending
            the offer. */}
        <GridItem colSpan={[1, null, 2]} display="flex" justifyContent="flex-end">
          <OutlineButton
            label="Create schedule"
            className={undefined}
            onClick={submit}
          />
        </GridItem>
      </SimpleGrid>

      <div className="mb-6 mt-6 rounded-xl border p-4">
          <h4 className="text-sm font-bold text-brand-navy">Schedule</h4>

          {tableData.length == 0 && (
            <EmptyState.Root>
              <EmptyState.Content gap={4}>
                <EmptyState.Indicator>
                  <Image
                    src="/illustrations/empty_list.svg"
                    h="20"
                    w="20"
                    alt="Empty list"
                  />
                </EmptyState.Indicator>
                <EmptyState.Title>No schedule yet</EmptyState.Title>
                <EmptyState.Description>
                  Fill in the form above to create one.
                </EmptyState.Description>
              </EmptyState.Content>
            </EmptyState.Root>
          )}

          {tableData.length > 0 && (
            <>
              <Table.ScrollArea mt={5} maxH={"md"}>
                <Table.Root
                  // variant={"line"}
                  // borderWidth={"thin"}
                  color="fg"
                  interactive
                  stickyHeader
                >
                  <Table.Header>
                    <Table.Row borderBottomWidth={"thin"}>
                      <Table.ColumnHeader>Schedule</Table.ColumnHeader>
                      <Table.ColumnHeader>Amount</Table.ColumnHeader>
                      <Table.ColumnHeader>Start Date</Table.ColumnHeader>
                      <Table.ColumnHeader>End Date</Table.ColumnHeader>
                      <Table.ColumnHeader>Status</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {
                      //                 <p>{each.description}</p>,
                      // <p>${commaSeperatedNumberFormat(each.amountPayable)}</p>,
                      // <p>{format(new Date(each?.startDate ?? Date.now()), "EEE, MMM d")}</p>,
                      // <p>{format(new Date(each?.endDate ?? Date.now()), "EEE, MMM d")}</p>,
                      // <Status label={"open"} />,
                      // <X onClick={handleDelete} />,
                      tableData.map((data, index) => {
                        return (
                          <Table.Row key={index} borderBottomWidth={"thin"}>
                            <Table.Cell>{data.description}</Table.Cell>
                            <Table.Cell>
                              {/* {currencySymbol} */}
                                     { user?.userCompany.currency  ?? '₦' }{" "}{commaSeperatedNumberFormat(data.amountPayable)}
                            </Table.Cell>
                            <Table.Cell>
                              {format(
                                new Date(data?.startDate ?? Date.now()),
                                "EEE, MMM d"
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              {format(
                                new Date(data?.endDate ?? Date.now()),
                                "EEE, MMM d"
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              <Status label={"open"} />
                            </Table.Cell>
                            <Table.Cell>
                              <IconButton onClick={handleDelete} variant="ghost" size="sm" color="fg" aria-label="Remove schedule">
                                <LuX />
                              </IconButton>
                            </Table.Cell>
                          </Table.Row>
                        );
                      })
                    }
                  </Table.Body>
                </Table.Root>
              </Table.ScrollArea>

              {/* {timelineValidation === "overflow" && (
                        <p className="text-red-400 text-sm italic pt-5">
                          Duration in days does not align with given duration
                        </p>
                      )}
      
                      {timelineValidation === "invalid" && (
                        <p className="text-red-400 text-sm italic pt-5">
                          Invalid date selection
                        </p>
                      )}
      
                      {!costValidation && (
                        <p className="text-red-400 text-sm italic pt-5">
                          Project budget has exceeded actual budget
                        </p>
                      )} */}
            </>
          )}
      </div>

      {tableData.length !== 0 && (
        <OfferActions
          requiresDeposit={requiresDeposit}
          percentage={percentage}
          currency={user?.userCompany?.currency ?? "₦"}
          balance={balance}
          onFundWallet={() => setDepositOpen(true)}
          fundLoading={isLoading}
          submitLabel={isBasic ? "Give Offer" : "Next"}
          onSubmit={isBasic ? createOffer : onNext}
          submitLoading={isLoading}
        />
      )}

      {depositOpen /**&& jobDetail */ && (
        <MakeDepositModal
          label="Confirm Your Deposit"
          info={`Proceed to make a payment of the displayed amount to the company account as shown below:`}
          close={() => setDepositOpen(false)}
          open={depositOpen}
          job={undefined /**jobDetail */}
          userId={userId}
          amount={balance}
          type="initial_deposit"
          loading={isLoading}
          confirmDeposit={() => setDepositOpen(false)}
        />
      )}

      {/* {paymentGatewayUrl !== "" && (
        <PaymentGatewayModal
          transactionId={transactionId}
          open={paymentGatewayUrl !== ""}
          close={() => {
            setPaymentGatewayUrl("");
          }}
          url={paymentGatewayUrl}
          callback={() => {
            setPaymentGatewayUrl("");
          }}
        />
      )} */}

      <CustomAlertDialog
        open={open}
        onOpenChange={setIsOpen}
        callback={createOffer}
        message="Ensure you have held virtual meeting with this talent before proceeding to give offer"
      />

      <span className="fixed right-3 top-3 z-30">
        <Alert />
      </span>
    </>
  );
};

export default RetainerForm;
